import { toast } from "react-toastify";
import AnexoSolicitacaoService from "../../services/resource/anexoSolicitacaoService";
import SolicitacaoService from "../../services/resource/solicitacaoService";
import { atualizarProgressoEnvio } from "./progressoEnvio";

const anexoService = new AnexoSolicitacaoService();
const solicitacaoService = new SolicitacaoService();

/**
 * Envia os anexos selecionados durante a criação (arquivo + descrição já staged no formulário)
 * depois que a solicitação foi criada e o ID retornado — a API só aceita upload após existir o
 * registro.
 *
 * Envio SEQUENCIAL (um de cada vez), não em paralelo: o proxy (nginx) limita a 2 conexões
 * simultâneas na rota de anexos (`limit_conn conn_import 2`) — enviar todos de uma vez fazia os
 * uploads além do limite serem rejeitados silenciosamente. Falhas individuais não interrompem os
 * demais uploads.
 */
export async function enviarAnexosStaged(solicitacaoId, itensStaged) {
    if (!itensStaged || itensStaged.length === 0) {
        return { sucesso: [], falha: [] };
    }

    const sucesso = [];
    const falha = [];

    for (let indice = 0; indice < itensStaged.length; indice++) {
        const item = itensStaged[indice];
        // Alimenta o carregamento em tela cheia da Nova Solicitação (ignorado fora dela).
        atualizarProgressoEnvio({
            subtitulo: "Enviando anexos...",
            anexoAtual: indice + 1,
            totalAnexos: itensStaged.length,
            arquivo: { nome: item.arquivo?.name, tamanho: item.arquivo?.size },
            percentualArquivo: 0,
        });
        try {
            await enviarComNovaTentativa(solicitacaoId, item);
            sucesso.push(item);
        } catch (erro) {
            falha.push({ ...item, erro: descreverErroEnvio(erro) });
            registrarFalhaNoHistorico(solicitacaoId, item, erro);
        }
    }

    atualizarProgressoEnvio({ subtitulo: "Concluindo...", anexoAtual: 0, totalAnexos: 0, arquivo: null });
    return { sucesso, falha };
}

// Falha de rede (sem resposta do servidor) costuma ser passageira — tenta mais uma vez antes de
// desistir. Erro com resposta (ex.: 413, 400) não adianta repetir.
async function enviarComNovaTentativa(solicitacaoId, item) {
    const onProgresso = percentualArquivo => atualizarProgressoEnvio({ percentualArquivo });
    try {
        return await anexoService.anexar(solicitacaoId, item.arquivo, item.descricao, onProgresso, true);
    } catch (erro) {
        if (erro.response) throw erro;
        atualizarProgressoEnvio({ percentualArquivo: 0, subtitulo: "Falha de conexão — tentando enviar o anexo novamente..." });
        return anexoService.anexar(solicitacaoId, item.arquivo, item.descricao, onProgresso, true);
    }
}

/** Texto curto do motivo da falha de upload — vai para o histórico da solicitação. */
export function descreverErroEnvio(erro) {
    const status = erro?.response?.status;
    if (status === 413) return "arquivo maior que o limite aceito pelo servidor (HTTP 413)";
    if (status) return `servidor recusou o arquivo (HTTP ${status}${erro.response.data?.message ? `: ${erro.response.data.message}` : ""})`;
    if (erro?.code === "ECONNABORTED" || /timeout/i.test(erro?.message || "")) return "tempo limite esgotado durante o envio";
    return `falha de conexão durante o envio${erro?.message ? ` (${erro.message})` : ""}`;
}

// Melhor esforço: se nem isso chegar ao servidor, o usuário já é avisado na tela pela lista de falhas.
function registrarFalhaNoHistorico(solicitacaoId, item, erro) {
    anexoService.registrarFalha(solicitacaoId, {
        nomeArquivo: item.arquivo?.name,
        descricao: item.descricao,
        tamanhoBytes: item.arquivo?.size || null,
        erro: descreverErroEnvio(erro),
    }).catch(() => { });
}

export function normalizarDescricao(texto = "") {
    return texto.normalize("NFD").replace(/\p{M}/gu, "").trim().toLowerCase();
}

/**
 * Descrições obrigatórias (ex.: ["Documento do Responsável"]) que ainda não têm anexo com a mesma
 * descrição — sem diferenciar maiúsculas/acentos, igual à validação do backend.
 */
export function tiposAnexoFaltando(obrigatorios = [], anexos = []) {
    const anexadas = new Set(anexos.map(a => normalizarDescricao(a.descricao)));
    return obrigatorios.filter(tipo => !anexadas.has(normalizarDescricao(tipo)));
}

/**
 * Para processos que o backend cria SEM enviar (anexos obrigatórios conferidos no envio): depois
 * que os anexos subiram, executa a ação ENVIAR. Devolve a solicitação atualizada ou `null` se o
 * envio foi recusado — nesse caso o usuário já é avisado de que ela ficou criada e pode ser
 * reenviada pela própria solicitação.
 */
export async function enviarAposAnexos(solicitacao, usuarioEscolhidoId) {
    atualizarProgressoEnvio({ subtitulo: "Enviando para a próxima etapa...", anexoAtual: 0, totalAnexos: 0, arquivo: null });
    try {
        const res = await solicitacaoService.executarAcao(solicitacao.id, { acao: "ENVIAR", usuarioEscolhidoId, dados: {} });
        return res.data || solicitacao;
    } catch (erro) {
        const mensagem = erro.response?.data?.message || "não foi possível enviá-la.";
        toast.error(`Solicitação ${solicitacao.numero} criada, mas não foi enviada: ${mensagem} Anexe os documentos na solicitação e envie novamente.`);
        return null;
    }
}

/**
 * Renomeia, antes do upload, os arquivos dos anexos cuja descrição é um dos `tipos` para
 * "PREFIXO - Tipo.ext" (ex.: "0001-0001 - Comprovante de Endereço.pdf"); repetidos ganham " (2)",
 * " (3)"... O backend aplica a mesma regra (FluxoProcesso#nomePadronizadoAnexo) como garantia.
 */
export function renomearAnexosPorTipo(itensStaged = [], tipos = [], prefixo) {
    const contagem = {};
    return itensStaged.map(item => {
        const tipo = tipos.find(t => normalizarDescricao(t) === normalizarDescricao(item.descricao));
        if (!tipo || !item.arquivo) return item;
        contagem[tipo] = (contagem[tipo] || 0) + 1;
        const nomeAtual = item.arquivo.name || "";
        const extensao = nomeAtual.includes(".") ? nomeAtual.slice(nomeAtual.lastIndexOf(".")).toLowerCase() : "";
        const sufixo = contagem[tipo] > 1 ? ` (${contagem[tipo]})` : "";
        const arquivo = new File([item.arquivo], `${prefixo} - ${tipo}${sufixo}${extensao}`, {
            type: item.arquivo.type, lastModified: item.arquivo.lastModified,
        });
        return { ...item, arquivo };
    });
}
